import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { showToast } from "@/redux/actions/toast-actions";
import { newCategorySchema } from "@/schemas/pricing-config.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const transformPayload = (values) => ({
  name: values.name,
  code: values.code,
  description: values.description,
});

const CategoryDialog = ({ open, onClose, mutationFn, category }) => {
  const isEditing = Boolean(category);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      name: category?.name ?? "",
      code: category?.code ?? "",
      description: category?.description ?? "",
    },
    enableReinitialize: true,
    validationSchema: newCategorySchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(transformPayload(values));
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(
        showToast(
          data?.message ??
            (isEditing ? "Category updated" : "New product category added"),
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["product-categories"] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {isEditing ? "Update Product Category" : "New Product Category"}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isEditing
              ? "Edit the details below to update this category."
              : "Fill in the details below to create a new product."}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormTextInput formik={formik} name="name" label="Category Name" />
          <FormTextInput formik={formik} name="code" label="Code" />
          <FormTextInput
            formik={formik}
            name="description"
            label="Description"
          />
        </Stack>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {mutation.error?.data?.[0] ?? "Failed. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
              ? "Update Category"
              : "Create Category"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CategoryDialog;
